import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowRight, ArrowLeft, Check, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/onboarding`;
// Explicit per-request header — a hard page load/refresh can mount this page's
// effect before App.js's own effect attaches axios.defaults' global Authorization
// header, which would otherwise 401 the very first request.
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STEPS = ['about_you', 'program', 'teams', 'goals', 'first_win'];
const STEP_LABELS = { about_you: 'About you', program: 'Your program', teams: 'Your teams', goals: 'Your goals', first_win: 'First win' };

const GOAL_OPTIONS = [
  { key: 'reduce_admin', label: 'Reduce admin work' },
  { key: 'stay_organized', label: 'Stay organized' },
  { key: 'practice_planning', label: 'Practice planning' },
  { key: 'game_planning', label: 'Game planning' },
  { key: 'team_communication', label: 'Team communication' },
  { key: 'scouting', label: 'Scouting' },
  { key: 'player_development', label: 'Player development' },
  { key: 'manage_multiple_teams', label: 'Manage multiple teams' },
];

export default function OnboardingPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const saveTimer = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/progress`, authHeaders()).then((r) => {
      const steps = r.data.steps || {};
      const firstIncomplete = STEPS.findIndex((s) => !steps[s]);
      // Spec: skip "First win" entirely once the account already has first
      // value — showing a "let's get your first win" screen to someone who
      // already achieved it doesn't make sense (e.g. backfilled customers).
      if (firstIncomplete === STEPS.length - 1 && r.data.first_value_at) {
        axios.post(`${API}/step/first_win`, { data: {} }, authHeaders()).catch(() => {});
        navigate('/');
        return;
      }
      setProgress(r.data);
      setAnswers(r.data.answers || {});
      setStepIdx(firstIncomplete === -1 ? STEPS.length - 1 : firstIncomplete);
    }).catch(() => toast.error('Could not load onboarding progress'));
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const currentKey = STEPS[stepIdx];
  const currentData = answers[currentKey] || {};

  const setField = (field, value) => {
    setAnswers((a) => ({ ...a, [currentKey]: { ...(a[currentKey] || {}), [field]: value } }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveStep({ ...(answers[currentKey] || {}), [field]: value }, false), 600);
  };

  const saveStep = async (data, advance) => {
    try {
      const res = await axios.post(`${API}/step/${currentKey}`, { data }, authHeaders());
      setProgress(res.data);
      if (advance) {
        const nextKey = STEPS[stepIdx + 1];
        // Same skip as load() above, but for the live in-wizard advance
        // (e.g. finishing "goals" when first value was achieved mid-wizard).
        if (nextKey === 'first_win' && res.data.first_value_at) {
          await axios.post(`${API}/step/first_win`, { data: {} }, authHeaders());
          navigate('/');
          return;
        }
        if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
        else navigate('/');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save — try again');
    }
  };

  if (!progress) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  const percent = Math.round(((stepIdx + (progress.steps?.[currentKey] ? 1 : 0)) / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-background flex items-center justify-center p-4">
      <div className="w-full max-w-xl space-y-6" data-testid="onboarding-wizard">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Step {stepIdx + 1} of {STEPS.length} — {STEP_LABELS[currentKey]}</p>
          <button className="text-xs text-muted-foreground underline" data-testid="onboarding-skip-link"
                  onClick={() => navigate('/')}>Do this later</button>
        </div>
        <Progress value={percent} data-testid="onboarding-progress-bar" />

        <Card className="border-2">
          <CardContent className="pt-6">
            {currentKey === 'about_you' && (
              <AboutYouStep data={currentData} setField={setField} onNext={() => saveStep(currentData, true)} />
            )}
            {currentKey === 'program' && (
              <ProgramStep data={currentData} setField={setField} onNext={() => saveStep(currentData, true)} onBack={() => setStepIdx(stepIdx - 1)} />
            )}
            {currentKey === 'teams' && (
              <TeamsStep data={currentData} setField={setField} onNext={() => saveStep(currentData, true)} onBack={() => setStepIdx(stepIdx - 1)} />
            )}
            {currentKey === 'goals' && (
              <GoalsStep data={currentData} setField={setField} onNext={() => saveStep(currentData, true)} onBack={() => setStepIdx(stepIdx - 1)} />
            )}
            {currentKey === 'first_win' && (
              <FirstWinStep progress={progress} onFinish={() => saveStep({}, true)} onBack={() => setStepIdx(stepIdx - 1)} navigate={navigate} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AboutYouStep({ data, setField, onNext }) {
  return (
    <div className="space-y-4" data-testid="onboarding-step-about-you">
      <div>
        <CardTitle>Tell us about you</CardTitle>
        <CardDescription>PAC uses this to personalize your Coach AI conversations.</CardDescription>
      </div>
      <div className="space-y-2">
        <Label>Preferred name</Label>
        <Input value={data.preferred_name || ''} data-testid="onboarding-preferred-name-input"
               onChange={(e) => setField('preferred_name', e.target.value)} placeholder="Coach Smith" />
      </div>
      <div className="space-y-2">
        <Label>Your role</Label>
        <Select value={data.coaching_role || ''} onValueChange={(v) => setField('coaching_role', v)}>
          <SelectTrigger data-testid="onboarding-coaching-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="head">Head coach</SelectItem>
            <SelectItem value="assistant">Assistant coach</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Phone (optional)</Label>
        <Input value={data.phone || ''} data-testid="onboarding-phone-input"
               onChange={(e) => setField('phone', e.target.value)} placeholder="(555) 123-4567" />
      </div>
      <Button className="w-full" data-testid="onboarding-about-you-next-btn" disabled={!data.preferred_name} onClick={onNext}>
        Continue <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}

function ProgramStep({ data, setField, onNext, onBack }) {
  return (
    <div className="space-y-4" data-testid="onboarding-step-program">
      <div>
        <CardTitle>Your program</CardTitle>
        <CardDescription>Helps Coach AI ground its answers in your program's context.</CardDescription>
      </div>
      <div className="space-y-2">
        <Label>School / program name</Label>
        <Input value={data.org_name || ''} data-testid="onboarding-org-name-input"
               onChange={(e) => setField('org_name', e.target.value)} placeholder="Lincoln High School" />
      </div>
      <div className="space-y-2">
        <Label>Sport</Label>
        <Input value={data.sport || ''} data-testid="onboarding-sport-input"
               onChange={(e) => setField('sport', e.target.value)} placeholder="Basketball" />
      </div>
      <div className="space-y-2">
        <Label>State</Label>
        <Input value={data.state || ''} data-testid="onboarding-state-input"
               onChange={(e) => setField('state', e.target.value)} placeholder="TX" />
      </div>
      <div className="space-y-2">
        <Label>Current season (optional)</Label>
        <Select value={data.current_season || 'auto'} onValueChange={(v) => setField('current_season', v)}>
          <SelectTrigger data-testid="onboarding-current-season-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Let PAC figure it out from the calendar</SelectItem>
            <SelectItem value="in">We're in-season right now</SelectItem>
            <SelectItem value="off">We're off-season right now</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Used to adjust how soon we flag an account as needing attention.</p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" data-testid="onboarding-program-back-btn" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Button className="flex-1" data-testid="onboarding-program-next-btn" disabled={!data.org_name || !data.sport} onClick={onNext}>
          Continue <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function TeamsStep({ data, setField, onNext, onBack }) {
  const teams = data.teams && data.teams.length ? data.teams : [{ name: '', level: '' }];
  const updateTeam = (i, field, value) => {
    const next = [...teams];
    next[i] = { ...next[i], [field]: value };
    setField('teams', next);
  };
  return (
    <div className="space-y-4" data-testid="onboarding-step-teams">
      <div>
        <CardTitle>Your team</CardTitle>
        <CardDescription>Add the team you coach — you can add more teams any time in Settings.</CardDescription>
      </div>
      <div className="space-y-2">
        <Label>Team name</Label>
        <Input value={teams[0].name || ''} data-testid="onboarding-team-name-input"
               onChange={(e) => updateTeam(0, 'name', e.target.value)} placeholder="Varsity Boys Basketball" />
      </div>
      <div className="space-y-2">
        <Label>Level</Label>
        <Input value={teams[0].level || ''} data-testid="onboarding-team-level-input"
               onChange={(e) => updateTeam(0, 'level', e.target.value)} placeholder="Varsity / JV / Freshman" />
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" data-testid="onboarding-teams-back-btn" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Button className="flex-1" data-testid="onboarding-teams-next-btn" disabled={!teams[0].name} onClick={onNext}>
          Continue <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function GoalsStep({ data, setField, onNext, onBack }) {
  const secondary = data.secondary || [];
  const toggleSecondary = (key) => {
    if (key === data.primary) return;
    const next = secondary.includes(key) ? secondary.filter((k) => k !== key) : [...secondary, key].slice(-2);
    setField('secondary', next);
  };
  return (
    <div className="space-y-4" data-testid="onboarding-step-goals">
      <div>
        <CardTitle>What matters most to you?</CardTitle>
        <CardDescription>Pick your top goal — this shapes what PAC recommends next.</CardDescription>
      </div>
      <div className="space-y-2">
        <Label>Primary goal</Label>
        <Select value={data.primary || ''} onValueChange={(v) => setField('primary', v)}>
          <SelectTrigger data-testid="onboarding-primary-goal-select"><SelectValue placeholder="Select your top goal" /></SelectTrigger>
          <SelectContent>{GOAL_OPTIONS.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-muted-foreground text-xs">Also interested in (up to 2)</Label>
        <div className="grid grid-cols-2 gap-2">
          {GOAL_OPTIONS.filter((g) => g.key !== data.primary).map((g) => (
            <label key={g.key} className="flex items-center gap-2 text-sm" data-testid={`onboarding-secondary-goal-${g.key}`}>
              <Checkbox checked={secondary.includes(g.key)} onCheckedChange={() => toggleSecondary(g.key)} />
              {g.label}
            </label>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" data-testid="onboarding-goals-back-btn" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Button className="flex-1" data-testid="onboarding-goals-next-btn" disabled={!data.primary} onClick={onNext}>
          Continue <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function FirstWinStep({ progress, onFinish, onBack, navigate }) {
  const rec = progress.recommendation || {};
  return (
    <div className="space-y-4 text-center" data-testid="onboarding-step-first-win">
      <Sparkles className="h-10 w-10 text-accent mx-auto" />
      <div>
        <CardTitle>You're set up — let's get your first win</CardTitle>
        <CardDescription>Based on your goal, we recommend:</CardDescription>
      </div>
      <div className="bg-muted/50 rounded-lg p-4">
        <p className="font-semibold" data-testid="onboarding-recommendation-title">{rec.title}</p>
      </div>
      <Button className="w-full" data-testid="onboarding-launch-recommendation-btn"
              onClick={() => { onFinish(); navigate(rec.path || '/'); }}>
        <Check className="h-4 w-4 mr-2" /> Let's do it
      </Button>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" data-testid="onboarding-first-win-back-btn" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Button variant="ghost" className="flex-1" data-testid="onboarding-first-win-dashboard-btn" onClick={onFinish}>
          Go to dashboard instead
        </Button>
      </div>
    </div>
  );
}
